import { useCallback, useContext, useEffect, useState } from "react"

// Context
import { UserContext } from "../../context/userContext"

//Styles
import dpStyles from "../../styles/dailyPlanner/DailyPlanner.module.css"

//DailyPlanner components
import {
  GetCounterPanel,
  GetSummaryPanel,
  GetDetailPanel,
} from "./DailyPlannerPanels"
import {
  getTasks,
  GetTaskPanel,
  GetNewTaskPanel,
  headerButtonAction,
} from "./DailyPlannerTasks"
import {
  getNotifications,
  GetNewNotificationsPanel,
  notificationHeaderButtonAction,
} from "./DailyPlannerNotifications"
import {
  getAppointments,
  GetAppointmentsPanel,
  headerButtonAction as appointmentsHeaderButtonAction,
  GetAppointmentInfo,
} from "./DailyPlannerAppointments"

//Notifications components:
import { useLazyQuery } from "@apollo/client"
import {
  GET_NOTIFICATION_USERS,
  GET_PARTICIPANT,
  GET_STAFF,
} from "../notifications/notificationsQueries"
import { LogError, LogWarning } from "../../helpers/logger"
import {
  documentsButtonAction,
  getDocuments,
} from "./DailyPlannerDocumentSigning"

export default function DailyPlannerPopup() {
  const userCtx = useContext(UserContext)
  const [initialLoad, setInitialLoad] = useState(true)

  //tasks
  const [isLoadingTasks, setIsLoadingTasks] = useState(true)
  const [taskCounter, setTaskCounter] = useState(undefined)
  const [newTasks, setNewTasks] = useState(undefined)
  const [tasksToday, setTasksToday] = useState(undefined)

  //Notifications
  const [isLoadingNotes, setIsLoadingNotes] = useState(true)
  const [newNotifications, setNewNotifications] = useState(true)
  const [notificationsClosed, setNewNotificationsClosed] = useState(new Date())

  //Apointments
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(true)
  const [appointmentsToday, setAppointmentsToday] = useState(undefined)
  const [showAppointmentInfo, setShowAppointmentInfo] = useState(undefined)

  //DocumentSigning
  const [documentsToSign, setDocumentsToSign] = useState(undefined)

  const [getNotificationUsers] = useLazyQuery(GET_NOTIFICATION_USERS, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
  })

  const [getNotificationStaff] = useLazyQuery(GET_STAFF, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: undefined,
    },
  })

  const [getParticipant] = useLazyQuery(GET_PARTICIPANT, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: undefined,
      participant_id: undefined,
      organization_id: undefined,
    },
  })

  useEffect(() => {
    if (!!userCtx.notification_closed) {
      if (userCtx.notification_closed > notificationsClosed) {
        setNewNotificationsClosed(new Date())
        RefreshTasks()
        RefreshNotifications()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.notification_closed])

  const RefreshTasks = useCallback(() => {
    setIsLoadingTasks(true)
    setTaskCounter(undefined)
    setNewTasks(undefined)
    setTasksToday(undefined)
    getTasks(userCtx, setTaskCounter, setNewTasks, setTasksToday)
    setIsLoadingTasks(false)
  }, [userCtx])

  const RefreshAppointments = useCallback(async () => {
    try {
      setIsLoadingAppointments(true)
      setAppointmentsToday(undefined)
      await getAppointments(userCtx, setAppointmentsToday)
      setIsLoadingAppointments(false)
    } catch (error) {
      setAppointmentsToday(undefined)
      LogError(error.message)
      userCtx.setNotificationError("Unable to load appointments")
      setIsLoadingAppointments(false)
    }
  }, [userCtx])

  const RefreshDocuments = useCallback(async () => {
    try {
      await getDocuments(userCtx, setDocumentsToSign)
    } catch (error) {
      setDocumentsToSign("Err")
      userCtx.setNotificationError("Unable to load documents to sign")
    }
  }, [userCtx])

  const RefreshNotifications = useCallback(() => {
    const notificationFindParticipant = async (
      participant_id,
      organization_id
    ) => {
      let result = undefined
      const participant = await getParticipant({
        variables: {
          staff_id: userCtx.session.user.staff_id,
          participant_id: participant_id,
          organization_id: organization_id,
          participant_alias: participant_id,
        },
      })

      if (!!participant.data.participant) {
        result = participant.data.participant
      } else if (!!participant.data.search) {
        const searchResult = participant.data.search.filter(
          (result) => result.participant_alias === participant_id
        )
        if (searchResult.length > 0) {
          result = searchResult[0]
        }
      }

      return result
    }

    const checkNotificationParticipantList = async (
      notification,
      participantList
    ) => {
      if (
        !!notification.caseId &&
        !participantList.some(
          (part) =>
            part.participant_id === notification.caseId ||
            part.participant_alias === notification.caseId
        )
      ) {
        let participantSearch = await notificationFindParticipant(
          notification.caseId,
          notification.groupId,
          participantList
        )

        if (!!participantSearch) {
          participantList = [...participantList, participantSearch]
        } else {
          LogWarning("Participant not found")
        }
      }

      return participantList
    }

    setIsLoadingNotes(true)
    setNewNotifications(undefined)
    getNotifications(
      userCtx,
      setNewNotifications,
      getNotificationUsers,
      checkNotificationParticipantList
    )
    setIsLoadingNotes(false)
  }, [getNotificationUsers, getParticipant, userCtx])

  useEffect(() => {
    if (initialLoad) {
      RefreshTasks()
      RefreshNotifications()
      RefreshAppointments()
      RefreshDocuments()
      setInitialLoad(false)
    }
  }, [
    RefreshTasks,
    RefreshNotifications,
    initialLoad,
    RefreshAppointments,
    RefreshDocuments,
  ])

  function taskHeaderButtonCallback() {
    headerButtonAction(userCtx)
  }

  function appointmentHeaderButtonCallback() {
    appointmentsHeaderButtonAction(userCtx)
  }

  function GetTaskTemplate(task) {
    return GetTaskPanel(task, userCtx, setIsLoadingTasks, RefreshTasks)
  }

  function GetNewTaskTemplate(task) {
    return GetNewTaskPanel(task, userCtx, setIsLoadingTasks, RefreshTasks)
  }

  function notificationsHeaderButtonCallback() {
    notificationHeaderButtonAction(userCtx)
  }

  function GetAppointmentTemplate(appointment) {
    return GetAppointmentsPanel(appointment, setShowAppointmentInfo)
  }

  return (
    <div className={dpStyles.mainGridContainer}>
      <GetAppointmentInfo
        appointment={showAppointmentInfo}
        onHideDialog={() => {
          setShowAppointmentInfo(undefined)
        }}
      />
      <div className={dpStyles.gridItemCounters}>
        <div className={dpStyles.counterGridContainer}>
          <div
            className={`${dpStyles.counterGridItemDocuments} ${dpStyles.CounterGridItem}`}
          >
            {GetCounterPanel(
              "Documents to sign",
              "orange",
              documentsToSign,
              () => {
                documentsButtonAction(userCtx)
              },
              "Go to Documents interface"
            )}
          </div>
          <div
            className={`${dpStyles.counterGridItemTasks} ${dpStyles.CounterGridItem}`}
          >
            {GetCounterPanel(
              "Tasks to complete",
              "salmon",
              taskCounter,
              taskHeaderButtonCallback,
              "Go to Tasks interface"
            )}
          </div>
          <div
            className={`${dpStyles.counterGridItemParticipantCaseLoad} ${dpStyles.CounterGridItem}`}
          >
            {GetCounterPanel("Participant caseload", "lightCyan", "N/A")}
          </div>
          <div
            className={`${dpStyles.counterGridItemReferrals} ${dpStyles.CounterGridItem}`}
          >
            {GetCounterPanel("Referrals to review", "DarkOrchid", "N/A")}
          </div>
        </div>
      </div>
      <div className={dpStyles.gridItemNewNotifications}>
        {GetSummaryPanel(
          "New notifications",
          "lightGreen",
          newNotifications,
          isLoadingNotes,
          GetNewNotificationsPanel,
          "No new notifications",
          notificationsHeaderButtonCallback,
          "Go to Notifications interface"
        )}
      </div>
      <div className={dpStyles.gridItemNewTasks}>
        {GetSummaryPanel(
          "New tasks",
          "salmon",
          newTasks,
          isLoadingTasks,
          GetNewTaskTemplate,
          "No new tasks",
          taskHeaderButtonCallback,
          "Go to Tasks interface"
        )}
      </div>
      <div className={dpStyles.gridItemNewParticipants}>
        {GetSummaryPanel("New participants", "lightCyan", [])}
      </div>
      <div className={dpStyles.gridItemTasksToday}>
        {GetDetailPanel(
          "Today's Tasks",
          "salmon",
          tasksToday,
          isLoadingTasks,
          GetTaskTemplate,
          "No tasks due today",
          taskHeaderButtonCallback,
          "Go to Tasks interface"
        )}
      </div>
      <div className={dpStyles.gridItemAppointmentToday}>
        {GetDetailPanel(
          "Today's Appointments",
          "orange",
          appointmentsToday,
          isLoadingAppointments,
          GetAppointmentTemplate,
          !appointmentsToday
            ? "Unable to retrieve appointments for today"
            : "No appointments due today",
          appointmentHeaderButtonCallback,
          "Go to Appointments interface"
        )}
      </div>
    </div>
  )
}
